import React from 'react';

export interface UseOutsideClickProps {
  enabled?: boolean;
  ref: React.RefObject<HTMLElement | null>;
  handler?: (e: Event) => void;
}

export const useClickAway = (props: UseOutsideClickProps) => {
  const { ref, handler, enabled = true } = props;
  const savedHandler = useCallbackRef(handler);

  const stateRef = React.useRef({
    isPointerDown: false,
    ignoreEmulatedMouseEvents: false,
  });

  const state = stateRef.current;

  React.useEffect(() => {
    if (!enabled) return;
    const onPointerDown = (e: PointerEvent) => {
      if (isValidEvent(e, ref)) {
        state.isPointerDown = true;
      }
    };

    const onMouseUp = (event: MouseEvent) => {
      if (state.ignoreEmulatedMouseEvents) {
        state.ignoreEmulatedMouseEvents = false;
        return;
      }

      if (state.isPointerDown && handler && isValidEvent(event, ref)) {
        state.isPointerDown = false;
        savedHandler(event);
      }
    };

    const onTouchEnd = (event: TouchEvent) => {
      state.ignoreEmulatedMouseEvents = true;
      if (handler && state.isPointerDown && isValidEvent(event, ref)) {
        state.isPointerDown = false;
        savedHandler(event);
      }
    };

    const doc = getOwnerDocument(ref.current);
    doc.addEventListener('mousedown', onPointerDown as EventListener, true);
    doc.addEventListener('mouseup', onMouseUp as EventListener, true);
    doc.addEventListener('touchstart', onPointerDown as EventListener, true);
    doc.addEventListener('touchend', onTouchEnd as EventListener, true);

    return () => {
      doc.removeEventListener('mousedown', onPointerDown as EventListener, true);
      doc.removeEventListener('mouseup', onMouseUp as EventListener, true);
      doc.removeEventListener('touchstart', onPointerDown as EventListener, true);
      doc.removeEventListener('touchend', onTouchEnd as EventListener, true);
    };
  }, [handler, ref, savedHandler, state, enabled]);
};

const isValidEvent = (event: Event, ref: React.RefObject<HTMLElement | null>): boolean => {
  const target = (event.composedPath?.()[0] ?? event.target) as HTMLElement;

  if (target) {
    const doc = getOwnerDocument(target);
    if (!doc.contains(target)) return false;
  }

  return !ref.current?.contains(target);
};

const getOwnerDocument = (node?: Element | null): Document => {
  return node?.ownerDocument ?? document;
};

const useCallbackRef = <T extends (...args: any[]) => any>(
  callback: T | undefined,
  deps: React.DependencyList = [],
) => {
  const callbackRef = React.useRef(callback);

  React.useEffect(() => {
    callbackRef.current = callback;
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: it's correct, just bad biome
  return React.useCallback(((...args) => callbackRef.current?.(...args)) as T, deps);
};
